import { BillingNotifications } from '@features/Admin/Billing/ui/BillingNotifications';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  IconBook2,
  IconChartBar,
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconCreditCard,
  IconFileText,
  IconPhoneFilled,
  IconLayoutDashboard,
  IconPhone,
  IconPlugConnected,
  IconRobot,
  IconSpeakerphone,
  IconSparkles,
  IconTool,
  IconUsers,
  IconWaveSine,
  IconX,
} from '@tabler/icons-react';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { actions } from '@shared/store/ducks';
import { useUserMeControllerUserMeQuery } from '@shared/services/apiService';
import { getVariantNavigation, type DesignNavItem, type DesignVariant } from './catalog';
import { useDesignStyles } from './styles';
import { DesignChooser } from './DesignChooser';
import { useDesignWorkspace } from './DesignWorkspaceProvider';
import Profile from '@shared/layouts/FullLayout/vertical/header/Profile';

const iconFor = (item: DesignNavItem) => {
  if (item.href === '/billing') return IconCreditCard;
  if (item.href === '/admin/access' || item.workspace === 'Audiences') return IconUsers;
  if (item.href === '/admin/campaign') return IconSpeakerphone;
  if (item.workspace === 'Agents' || item.href === '/admin/assistants') return IconRobot;
  if (item.workspace === 'Missions') return IconClock;
  if (item.workspace === 'Dashboard') return IconLayoutDashboard;
  if (item.workspace === 'Analytics') return IconChartBar;
  if (item.workspace === 'Knowledge base') return IconBook2;
  if (item.workspace === 'Connections') return IconPlugConnected;
  if (item.workspace === 'Tools') return IconTool;
  if (item.workspace === 'Phone numbers' || item.workspace === 'Call history') return IconPhone;
  return IconFileText;
};

export const DesignSidebar = ({ variant }: { variant: Exclude<DesignVariant, 'main'> }) => {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const mobile = useAppSelector((state) => Boolean(state.customizer.isMobileSidebar));
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const company = useAppSelector((state) => state.auth.authenticatedUserCompany);
  const { data: me } = useUserMeControllerUserMeQuery();
  const groups = getVariantNavigation(variant, me?.privileges);
  const [expanded, setExpanded] = useState(false);
  const workspace = useDesignWorkspace();
  const compact = desktop && ['v4', 'v5'].includes(variant) && !expanded;
  const width = compact
    ? variant === 'v5'
      ? 88
      : 72
    : variant === 'v3'
      ? 216
      : variant === 'v1'
        ? 224
        : variant === 'v5'
          ? 222
          : 232;
  const { classes, cx } = useDesignStyles();
  const closeNavigation = () => dispatch(actions.customizer.setMobileSidebar(false));
  useEffect(() => {
    dispatch(actions.customizer.setMobileSidebar(false));
  }, [pathname, variant, dispatch]);
  const content = (
    <Stack
      sx={{
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
        px: compact ? 1 : 2,
        py: 2,
        gap: compact ? 1.5 : 2.5,
        ...(variant === 'v4'
          ? {
              '& > :nth-child(1)': { order: 1 },
              '& > :nth-child(2)': { order: 0 },
              '& > :nth-child(n+3)': { order: 2 },
            }
          : {}),
      }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ minHeight: 36, justifyContent: compact ? 'center' : 'start' }}>
        <Box
          sx={{
            width: variant === 'v5' ? 29 : 34,
            height: variant === 'v5' ? 29 : 34,
            borderRadius: variant === 'v5' ? '50%' : 1.5,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}>
          {['v4', 'v5'].includes(variant) ? (
            <IconWaveSine size={20} />
          ) : (
            <IconPhoneFilled size={23} />
          )}
        </Box>
        {!compact && (
          <Typography variant="h3" fontWeight={750} sx={{ letterSpacing: -1.4 }}>
            callevate.
          </Typography>
        )}
      </Stack>
      <Box className={classes.workspace}>
        <Avatar
          variant="rounded"
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'primary.light',
            color: 'primary.main',
            fontSize: 12,
          }}>
          {company?.title?.slice(0, 1) || 'C'}
        </Avatar>
        {!compact && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {variant === 'v1' ? 'Company' : `Workspace · ${variant.toUpperCase()}`}
            </Typography>
            <Typography variant="body2" fontWeight={600} noWrap>
              {company?.title || 'Current company'}
            </Typography>
          </Box>
        )}
      </Box>
      <Stack
        component="nav"
        aria-label={`${variant.toUpperCase()} navigation`}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: compact ? 'none' : 'thin',
          '&::-webkit-scrollbar': { width: compact ? 0 : 6 },
        }}
        gap={2}>
        {groups
          .filter((group) => !(compact && variant === 'v5' && group.label === 'Account'))
          .map((group) => (
            <Box key={group.label}>
              {!compact && (
                <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                  {group.label}
                </Typography>
              )}
              <Stack gap={0.5}>
                {group.items
                  .filter(
                    (item) =>
                      !(
                        compact &&
                        variant === 'v5' &&
                        ['Jobs', 'Knowledge base', 'Tools', 'Phone numbers'].includes(item.label)
                      ),
                  )
                  .map((item) => {
                    const Icon = iconFor(item);
                    const selected = item.workspace
                      ? workspace.module === item.workspace ||
                        (!workspace.module &&
                          item.workspace === 'Agents' &&
                          pathname.startsWith('/workspace/campaign/'))
                      : !workspace.module &&
                        item.href &&
                        (pathname === item.href || pathname.startsWith(item.href + '/'));
                    const body = (
                      <>
                        <Icon size={18} stroke={1.6} />
                        <Box
                          component="span"
                          sx={{
                            minWidth: 0,
                            flex: compact ? undefined : 1,
                            textAlign: compact ? 'center' : 'start',
                            overflowWrap: compact ? 'normal' : 'anywhere',
                          }}>
                          {compact && variant !== 'v5'
                            ? null
                            : compact && item.workspace === 'Call history'
                              ? 'History'
                              : compact && item.workspace === 'Audiences'
                                ? 'Audience'
                                : item.label}
                        </Box>
                      </>
                    );
                    return (
                      <Tooltip
                        key={item.label}
                        title={
                          compact ? `${item.label}${item.workspace ? ' · Workspace' : ''}` : ''
                        }
                        placement={theme.direction === 'rtl' ? 'left' : 'right'}>
                        {item.href ? (
                          <Button
                            className={cx(classes.navButton, compact && classes.compactButton)}
                            component={Link}
                            to={item.href}
                            aria-label={item.label}
                            aria-current={selected ? 'page' : undefined}
                            onClick={(event) => {
                              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
                                return;
                              event.preventDefault();
                              closeNavigation();
                              workspace.navigate(item.href!);
                            }}>
                            {body}
                          </Button>
                        ) : (
                          <Button
                            className={cx(classes.navButton, compact && classes.compactButton)}
                            aria-label={`${item.label} workspace`}
                            aria-current={selected ? 'page' : undefined}
                            onClick={() => {
                              closeNavigation();
                              workspace.open(item.workspace!);
                            }}>
                            {body}
                          </Button>
                        )}
                      </Tooltip>
                    );
                  })}
              </Stack>
            </Box>
          ))}
      </Stack>
      <Button
        className={cx(classes.navButton, compact && classes.compactButton)}
        aria-label="Ask Callevate workspace"
        aria-expanded={workspace.assistantOpen}
        onClick={() => {
          closeNavigation();
          workspace.open('Ask Callevate');
        }}>
        <IconSparkles size={18} />
        {!compact && 'Ask Callevate'}
      </Button>
      <Divider />
      <DesignChooser compact={compact} />
      {desktop && ['v4', 'v5'].includes(variant) && (
        <Button
          className={classes.navButton}
          aria-label={compact ? 'Expand navigation' : 'Collapse navigation'}
          onClick={() => setExpanded((value) => !value)}>
          {compact ? (
            <IconChevronRight size={18} />
          ) : (
            <>
              <IconChevronLeft size={18} />
              Collapse
            </>
          )}
        </Button>
      )}
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent={compact ? 'center' : 'start'}>
        {desktop && ['v2', 'v3'].includes(variant) && <BillingNotifications />}
        <Profile drawer />
        {!compact && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {user?.firstName || 'Account'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Account & preferences
            </Typography>
          </Box>
        )}
      </Stack>
      {!desktop && (
        <Button startIcon={<IconX size={18} />} onClick={closeNavigation}>
          Close navigation
        </Button>
      )}
    </Stack>
  );
  return (
    <>
      <Box sx={{ width: desktop ? width : 0, flexShrink: 0 }}>
        <Drawer
          variant={desktop ? 'permanent' : 'temporary'}
          open={desktop || mobile}
          onClose={closeNavigation}
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          PaperProps={{
            sx: {
              width: desktop ? width : 260,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              bgcolor: ['v3', 'v4', 'v5'].includes(variant)
                ? 'background.default'
                : 'background.paper',
              borderColor: 'divider',
              '& nav': { gap: compact && variant === 'v5' ? 2 : undefined },
            },
          }}>
          {content}
        </Drawer>
      </Box>
    </>
  );
};
